import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { ApplicationPackage } from './application-package.service';
import { ProjectFacilitySponsorApi } from './project-facility-sponsor.service';

import { NgbPanelChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class PermitProjectFacilityAopService {

  private projectFacilityAopsSource = new BehaviorSubject<ProjectFacilityAnnualOperatingPlanApi[]>([]);
  currentProjectFacilityAops = this.projectFacilityAopsSource.asObservable();

  private projectFacilityAopSelectedSource = new BehaviorSubject<ProjectFacilityAnnualOperatingPlanApi>(null);
  currentProjectFacilityAopSelected = this.projectFacilityAopSelectedSource.asObservable();
  
  private projectFacilityAopSource = new BehaviorSubject<ProjectFacilityAnnualOperatingPlanApi>(null);
  currentProjectFacilityAop = this.projectFacilityAopSource.asObservable();

  private projectFacilityAccordionSource = new BehaviorSubject<any>({});
  currentProjectFacilityAccordion = this.projectFacilityAccordionSource.asObservable();
  private projectFacilityAccordion = {};

  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string,
    private toastr: ToastrService
  ) { }

  getPfaops(permitAnnualOperatingPlanId: number) {
    this.http.get<ProjectFacilityAnnualOperatingPlanApi[]>(`${this.baseUrl}api/projectfacilityannualoperatingplan/search?PermitAnnualOperatingPlanId=${permitAnnualOperatingPlanId}`).subscribe(
      response => {
        if (response.length) {
          this.changePfaops(response);
          response.forEach(aop => {
            this.changeAcc(aop.projectFacilityAnnualOperatingPlanId);
          });
        } else {
          const empty = [] as ProjectFacilityAnnualOperatingPlanApi[];
          this.changePfaops(empty);
        }
      },
      error => console.error(error)
    );

  }

  putPfaop(formData: ProjectFacilityAnnualOperatingPlanApi) {
    this.http.put<ProjectFacilityAnnualOperatingPlanApi>(`${this.baseUrl}api/projectfacilityannualoperatingplan`, formData, {observe: 'response'}).subscribe(response => {
      if (response.status === 200) {
        this.getPfaops(formData.permitAnnualOperatingPlanId);
        this.toastr.success("Recharge Facility Saved Successfully.");
      }
    });
  }

  postPfaop(formData: ProjectFacilityAnnualOperatingPlanApi) {
    this.http.post<ProjectFacilityAnnualOperatingPlanApi>(`${this.baseUrl}api/projectfacilityannualoperatingplan`, formData, {observe: 'response'}).subscribe(response => {
      this.getPfaops(formData.permitAnnualOperatingPlanId);
    });
  }

  changePfaops(aops: ProjectFacilityAnnualOperatingPlanApi[]) {
    this.projectFacilityAopsSource.next(aops);
    aops.forEach(aop => {
      this.changeAcc(aop.projectFacilityAnnualOperatingPlanId);
    });
  }

  changePfaop(aop: ProjectFacilityAnnualOperatingPlanApi) {
    this.projectFacilityAopSource.next(aop);
  }

  changePfaopSelected(aop: ProjectFacilityAnnualOperatingPlanApi) {
    this.projectFacilityAopSelectedSource.next(aop);
  }

  changeAcc(id: number, panelId?: string, nextState?: boolean) {
    let temp = this.projectFacilityAccordion;
    // Check if id exists first
    if (id in temp) {
      if (panelId) {
        // If the id exists, check if the array contains the panel that is supposed to be closed, and remove if necessary
        if ((temp[id].includes(panelId)) && (nextState === false)) {
          temp[id] = temp[id].filter(x => x !== panelId);
        // Check if the array contains the panel that is supposed to be open, and add if necessary
        } else if (!(this.projectFacilityAccordion[id].includes(panelId)) && (nextState === true)) {
          temp[id].push(panelId);
        }
      }
    // Add the id with an empty array if it does not exist yet.
    } else {
      this.projectFacilityAccordion[id] = [];
    }
    this.projectFacilityAccordionSource.next(temp);
  }

  changeAccByEvent(id:number, $event: NgbPanelChangeEvent) {
    this.changeAcc(id, $event.panelId, $event.nextState);
  }

  changeAccByArr(id: number, arr: string[]) {
    let temp = this.projectFacilityAccordion;
    temp[id] = arr;
    this.projectFacilityAccordionSource.next(temp);
  }

}

export interface ProjectFacilityAnnualOperatingPlanApi
{
    projectFacilityAnnualOperatingPlanId?: number, // int
    permitAnnualOperatingPlanId: number, // int
    projectFacilityId?: number, // int

    //Location & Capacity Fields (Disabled on UI Populated from the associated facility object
    projectFacilityPermitPointOfDiversionId?: number, // int
    projectFacilityListFacilityTypeId: number, // int
    projectFacilityListFacilityTypeName: string, // string
    projectFacilityName?: string, // string
    projectFacilityDescription?: string, // string
    projectFacilityDeliveryPointLatitude?: number, // double
    projectFacilityDeliveryPointLongitude?: number, // double

    operationalStart?: Date, // DateTime
    operationalEnd?: Date, // DateTime
    operationalNarrative?: string, // string
    diversionRate?: number, // double
    deliveryRate?: number, // double
    annualMaxCapacityForAllDiversionsAF?: number, // double
    rechargeMaxOperationalHeadFT?: number, // double
    rechargeMaxSurfaceAreaSFT?: number, // double
    engineeringDrawingsAvailable?: boolean, // bool

    // Partners/Sponsors List
    hasSponsors: boolean,
    projectFacilitySponsorApiList: ProjectFacilitySponsorApi[], // List<ProjectFacilitySponsorApi>

    // Instrumentation List
    hasInstrumentation: boolean,
    projectFacilityInstrumentApiList?: ProjectFacilityInstrumentApi[], // List<ProjectFacilityInstrumentApi>

    canEdit?: boolean, // bool
}

export interface ProjectFacilityInstrumentApi
{
    projectFacilityInstrumentId?: number, // int
    projectFacilityAnnualOperatingPlanId?: number, // int
    listInstrumentTypeId?: number, // int
    listInstrumentTypeName?: string, // string
    name?: string, // string
    locationLatitude?: number, // double
    locationLongitude?: number, // double
    listRecorderTypeId?: number, // int
    listRecorderTypeName?: string, // string
    listDeviceRecordingIntervalId?: number, // int
    listDeviceRecordingIntervalName?: string, // string
    isAvailableToNeDNRLiveDataFeed?: boolean, // bool
    isAvailableToNeDNRStageToSourceTable?: boolean, // bool
    isAvailableToNeDNRAnnualDataSummary?: boolean, // bool
}